import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Button, Card, Field, Input, Modal, PageHeader, Select } from '../../design';
import { useToast } from '../../design/Toast';
import { TYPE_LABEL, type LockerType } from '../lockers/common';
import { useLockerOptions } from '../operations/common';
import { useOrgOptions } from '../users/UsersPage';
import s from './automations.module.css';

export type Rule = components['schemas']['AutomationRule'];
export type CatalogEntry = components['schemas']['RuleCatalogEntry'];
export type RuleAction = components['schemas']['RuleAction'];
type Channel = components['schemas']['NotificationChannel'];

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrator',
  manager: 'Manager',
  operator: 'Operator',
  tech: 'Tehnician',
  auditor: 'Auditor',
};

export function useChannels() {
  return useQuery({
    queryKey: ['settings', 'channels'],
    queryFn: () => unwrap(api.GET('/settings/channels')),
    staleTime: 60_000,
  });
}

/** Modulul Automatizări (D2, D5): catalogul fix R1–R6, configurat per organizație. */
export function AutomationsPage() {
  const { user, has } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const orgs = useOrgOptions();
  const [orgChoice, setOrgChoice] = useState<string>('');
  const effectiveOrg = user?.orgId ?? (orgChoice || orgs.data?.items[0]?.id || '');
  const canEdit = has('rules');

  const catalog = useQuery({
    queryKey: ['automation-catalog'],
    queryFn: () => unwrap(api.GET('/automation-rules/catalog')),
    staleTime: 300_000,
  });
  const rules = useQuery({
    queryKey: ['automation-rules', effectiveOrg],
    queryFn: () =>
      unwrap(
        api.GET('/automation-rules', {
          params: { query: effectiveOrg ? { orgId: effectiveOrg } : {} },
        }),
      ),
    enabled: !!effectiveOrg || user?.orgId !== null,
  });
  const channels = useChannels();
  const [editing, setEditing] = useState<{ entry: CatalogEntry; rule: Rule } | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['automation-rules'] });
  const toggle = useMutation({
    mutationFn: (r: Rule) =>
      unwrap(
        api.PATCH('/automation-rules/{id}', {
          params: { path: { id: r.id } },
          body: { enabled: !r.enabled },
        }),
      ),
    onSuccess: (r) => {
      toast(r.enabled ? `${r.name}: activată.` : `${r.name}: dezactivată.`, 'success');
      invalidate();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const create = useMutation({
    mutationFn: (kind: Rule['kind']) =>
      unwrap(api.POST('/automation-rules', { body: { kind, orgId: effectiveOrg || undefined } })),
    onSuccess: () => {
      toast('Regula a fost creată cu setările implicite.', 'success');
      invalidate();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const test = useMutation({
    mutationFn: (r: Rule) =>
      unwrap(api.POST('/automation-rules/{id}/test', { params: { path: { id: r.id } } })),
    onSuccess: (d) =>
      toast(
        d.status === 'sent'
          ? 'Mesajul de test a fost trimis pe e-mailul tău.'
          : `Livrarea de test are starea „${d.status}” — vezi Livrări.`,
        d.status === 'sent' ? 'success' : 'error',
      ),
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const orgRules = (rules.data ?? []).filter((r) => r.orgId === effectiveOrg);
  const globalRules = (rules.data ?? []).filter((r) => r.orgId === null);

  return (
    <>
      <PageHeader
        title="Automatizări"
        subtitle="Reguli de notificare și intervenție, configurabile pe tip de locker și perimetru. Regulile rulează pe server; canalele se pornesc din Setări."
      />
      {user?.orgId === null && (
        <div className="flex wrap" style={{ marginBottom: 16 }}>
          <Select
            value={effectiveOrg}
            onChange={(e) => setOrgChoice(e.target.value)}
            style={{ maxWidth: 280 }}
            aria-label="Organizație"
          >
            {orgs.data?.items.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
          {channels.data && !channels.data.smsEnabled && (
            <Badge tone="neutral">SMS dezactivat global (Setări → Canale)</Badge>
          )}
        </div>
      )}
      {(catalog.error || rules.error) && (
        <p style={{ color: 'var(--red)' }}>{errorMessage(catalog.error ?? rules.error)}</p>
      )}
      <div className={s.grid}>
        {(catalog.data ?? []).map((entry) => {
          const rule = orgRules.find((r) => r.kind === entry.kind);
          const globalRule = globalRules.find((r) => r.kind === entry.kind);
          const future = entry.availableFrom > 20;
          return (
            <Card key={entry.kind}>
              <div className="flex between">
                <span className={s.kind}>{entry.kind}</span>
                {rule ? (
                  <label className={s.switch}>
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      disabled={!canEdit || future || toggle.isPending}
                      onChange={() => toggle.mutate(rule)}
                      aria-label={`Activează ${entry.name}`}
                    />
                    <i />
                  </label>
                ) : (
                  <Badge tone="neutral">neconfigurată</Badge>
                )}
              </div>
              <h3 style={{ marginTop: 12 }}>{entry.name}</h3>
              <p className="muted small">{entry.description}</p>
              <div className={s.flow}>
                <span>{entry.trigger}</span>
                <span aria-hidden="true">→</span>
                <span>{entry.action}</span>
              </div>
              {rule && <ActionsSummary rule={rule} />}
              {globalRule && !rule && (
                <p className="small muted">Se aplică regula globală a platformei.</p>
              )}
              <div className="flex between" style={{ marginTop: 12 }}>
                <span className="small muted">
                  {future
                    ? 'Disponibilă într-un plan viitor'
                    : rule
                      ? scopeLabel(rule)
                      : 'Fără regulă pentru această organizație'}
                </span>
                {canEdit && !future && (
                  <span className="flex">
                    {rule ? (
                      <>
                        <Button small onClick={() => setEditing({ entry, rule })}>
                          Configurează
                        </Button>
                        <Button
                          small
                          variant="ghost"
                          icon="mail"
                          onClick={() => test.mutate(rule)}
                          disabled={test.isPending}
                        >
                          Test
                        </Button>
                      </>
                    ) : (
                      <Button small variant="primary" onClick={() => create.mutate(entry.kind)}>
                        Creează
                      </Button>
                    )}
                  </span>
                )}
              </div>
            </Card>
          );
        })}
      </div>
      {editing && (
        <RuleDialog
          entry={editing.entry}
          rule={editing.rule}
          orgId={effectiveOrg}
          smsEnabled={!!channels.data?.smsEnabled}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}
    </>
  );
}

function scopeLabel(rule: Rule): string {
  const parts: string[] = [];
  if (rule.scope.lockerTypes.length)
    parts.push(rule.scope.lockerTypes.map((t) => TYPE_LABEL[t as LockerType]).join(', '));
  if (rule.scope.lockerIds.length) parts.push(`${rule.scope.lockerIds.length} lockere alese`);
  return parts.length ? parts.join(' · ') : 'Toate lockerele';
}

function ActionsSummary({ rule }: { rule: Rule }) {
  const bits: string[] = [];
  for (const a of rule.actions) {
    const ch = a.channels.join(' + ') || '—';
    if (a.type === 'notify-recipient') bits.push(`destinatar (${ch})`);
    else {
      const who = [
        ...(a.roleKeys ?? []).map((r) => ROLE_LABEL[r] ?? r),
        ...((a.userIds ?? []).length ? [`${a.userIds!.length} utilizatori`] : []),
        ...((a.emails ?? []).length ? [`${a.emails!.length} adrese`] : []),
        ...((a.phones ?? []).length ? [`${a.phones!.length} telefoane`] : []),
      ];
      bits.push(`${who.join(', ') || 'nimeni'} (${ch})`);
    }
  }
  return (
    <p className="small" style={{ marginTop: 8 }}>
      <span className="muted">Notifică: </span>
      {bits.join('; ') || 'nimeni'}
    </p>
  );
}

const ALL_TYPES = Object.keys(TYPE_LABEL) as LockerType[];

function RuleDialog({
  entry,
  rule,
  orgId,
  smsEnabled,
  onClose,
  onSaved,
}: {
  entry: CatalogEntry;
  rule: Rule;
  orgId: string;
  smsEnabled: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const lockers = useLockerOptions(orgId || undefined);
  const usersAction = rule.actions.find((a) => a.type === 'notify-users');
  const recipientAction = rule.actions.find((a) => a.type === 'notify-recipient');
  const [types, setTypes] = useState<LockerType[]>(rule.scope.lockerTypes as LockerType[]);
  const [lockerIds, setLockerIds] = useState<string[]>(rule.scope.lockerIds);
  const [params, setParams] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(rule.params).map(([k, v]) => [k, String(v)])),
  );
  const [roles, setRoles] = useState<string[]>(usersAction?.roleKeys ?? []);
  const [emails, setEmails] = useState((usersAction?.emails ?? []).join('\n'));
  const [phones, setPhones] = useState((usersAction?.phones ?? []).join('\n'));
  const [userChannels, setUserChannels] = useState<Channel[]>(
    (usersAction?.channels as Channel[] | undefined) ?? ['email'],
  );
  const [notifyRecipient, setNotifyRecipient] = useState(!!recipientAction);
  const [recipientChannels, setRecipientChannels] = useState<Channel[]>(
    (recipientAction?.channels as Channel[] | undefined) ?? ['email'],
  );
  const canUsers = entry.targets.includes('users');
  const canRecipient = entry.targets.includes('recipient');

  const save = useMutation({
    mutationFn: () => {
      const actions: RuleAction[] = [];
      if (canUsers) {
        actions.push({
          type: 'notify-users',
          roleKeys: roles as RuleAction['roleKeys'],
          userIds: [],
          emails: splitLines(emails),
          phones: smsEnabled ? splitLines(phones) : [],
          channels: userChannels.filter((c) => c === 'email' || smsEnabled),
        });
      }
      if (canRecipient && notifyRecipient) {
        actions.push({
          type: 'notify-recipient',
          channels: recipientChannels.filter((c) => c === 'email' || smsEnabled),
        });
      }
      const numericParams: Record<string, number> = {};
      for (const p of entry.params) {
        const v = params[p.key];
        if (v !== undefined && v !== '') numericParams[p.key] = Number(v);
      }
      return unwrap(
        api.PATCH('/automation-rules/{id}', {
          params: { path: { id: rule.id } },
          body: { scope: { lockerTypes: types, lockerIds }, params: numericParams, actions },
        }),
      );
    },
    onSuccess: () => {
      toast('Regula a fost salvată.', 'success');
      onSaved();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const toggleIn = <T,>(list: T[], v: T, set: (l: T[]) => void) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <Modal
      title={`${entry.kind} · ${entry.name}`}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          <Button variant="primary" onClick={() => save.mutate()} busy={save.isPending}>
            Salvează regula
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="small muted">{entry.description}</p>
        <h3>Perimetru</h3>
        <Field label="Tipuri de locker" hint="Niciunul bifat = toate tipurile">
          <div className="flex wrap">
            {ALL_TYPES.map((t) => (
              <label key={t} className="flex small">
                <input
                  type="checkbox"
                  checked={types.includes(t)}
                  onChange={() => toggleIn(types, t, setTypes)}
                />
                {TYPE_LABEL[t]}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Doar anumite lockere" hint="Niciunul bifat = toate lockerele organizației">
          <div className={s.lockerList}>
            {(lockers.data?.items ?? [])
              .filter((l) => !orgId || l.orgId === orgId)
              .map((l) => (
                <label key={l.id} className="flex small">
                  <input
                    type="checkbox"
                    checked={lockerIds.includes(l.id)}
                    onChange={() => toggleIn(lockerIds, l.id, setLockerIds)}
                  />
                  {l.name}
                </label>
              ))}
            {lockers.data && lockers.data.items.length === 0 && (
              <span className="small muted">Organizația nu are lockere.</span>
            )}
          </div>
        </Field>

        {entry.params.length > 0 && (
          <>
            <h3>Parametri</h3>
            <div className="flex wrap">
              {entry.params.map((p) => (
                <Field
                  key={p.key}
                  label={`${p.label} (${p.unit})`}
                  hint={
                    p.fallback
                      ? `Gol = valoarea din regulile fiecărui locker · ${p.min}–${p.max}`
                      : `${p.min}–${p.max}`
                  }
                  htmlFor={`rule-${p.key}`}
                >
                  <Input
                    id={`rule-${p.key}`}
                    type="number"
                    min={p.min}
                    max={p.max}
                    value={params[p.key] ?? ''}
                    onChange={(e) => setParams({ ...params, [p.key]: e.target.value })}
                  />
                </Field>
              ))}
            </div>
          </>
        )}

        <h3>Acțiuni</h3>
        {canUsers && (
          <div className="stack">
            <Field
              label="Roluri notificate"
              hint="Utilizatorii activi ai organizației cu aceste roluri"
            >
              <div className="flex wrap">
                {Object.entries(ROLE_LABEL).map(([k, label]) => (
                  <label key={k} className="flex small">
                    <input
                      type="checkbox"
                      checked={roles.includes(k)}
                      onChange={() => toggleIn(roles, k, setRoles)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Adrese suplimentare" hint="Câte una pe linie" htmlFor="rule-emails">
              <textarea
                id="rule-emails"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                rows={2}
                className={s.textarea}
              />
            </Field>
            {smsEnabled && (
              <Field
                label="Telefoane suplimentare"
                hint="Câte unul pe linie, ex. +40 700 000 000"
                htmlFor="rule-phones"
              >
                <textarea
                  id="rule-phones"
                  value={phones}
                  onChange={(e) => setPhones(e.target.value)}
                  rows={2}
                  className={s.textarea}
                />
              </Field>
            )}
            <Field label="Canale (utilizatori)">
              <div className="flex wrap">
                <label className="flex small">
                  <input
                    type="checkbox"
                    checked={userChannels.includes('email')}
                    onChange={() => toggleIn(userChannels, 'email', setUserChannels)}
                  />
                  E-mail
                </label>
                {smsEnabled && (
                  <label className="flex small">
                    <input
                      type="checkbox"
                      checked={userChannels.includes('sms')}
                      onChange={() => toggleIn(userChannels, 'sms', setUserChannels)}
                    />
                    SMS
                  </label>
                )}
              </div>
            </Field>
          </div>
        )}
        {canRecipient && (
          <div className="stack">
            <label className="flex small">
              <input
                type="checkbox"
                checked={notifyRecipient}
                onChange={(e) => setNotifyRecipient(e.target.checked)}
              />
              Notifică destinatarul operațiunii
            </label>
            {notifyRecipient && (
              <Field
                label="Canale (destinatar)"
                hint={smsEnabled ? undefined : 'SMS-ul este dezactivat global'}
              >
                <div className="flex wrap">
                  <label className="flex small">
                    <input
                      type="checkbox"
                      checked={recipientChannels.includes('email')}
                      onChange={() => toggleIn(recipientChannels, 'email', setRecipientChannels)}
                    />
                    E-mail
                  </label>
                  {smsEnabled && (
                    <label className="flex small">
                      <input
                        type="checkbox"
                        checked={recipientChannels.includes('sms')}
                        onChange={() => toggleIn(recipientChannels, 'sms', setRecipientChannels)}
                      />
                      SMS
                    </label>
                  )}
                </div>
              </Field>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function splitLines(v: string): string[] {
  return v
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}
